import {
  Badge,
  Button,
  Card,
  Group,
  SegmentedControl,
  Stack,
  Switch,
  Text,
  Title,
  UnstyledButton,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import type { ColumnDef, PaginationState } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useMyRoles, usePermission, useScope } from "@how/app-kit";
import { semanticColor } from "@how/design-system";
import { DataTable, FilterBar, LoadingState, PageHeader, useReturnFocus } from "@how/ui";
import { InviteMemberModal } from "./InviteMemberModal";
import { MemberAccessModal, isApiKeyLogin, memberName } from "./MemberAccessModal";
import { isPrivilegedRole, roleName } from "./role-catalog";
import { TeamTabs } from "./TeamTabs";
import {
  describeTeamError,
  useActiveCampuses,
  useCampusLimit,
  useCustomRoles,
  useSetCampusLimitsStaff,
  useTeam,
  type CustomRole,
  type TeamMember,
} from "./team-queries";

/** Roles that make someone part of the church's team rather than only a portal member. */
const PORTAL_ONLY = new Set(["member", "guardian"]);

type Show = "team" | "everyone";

const NO_ROLES: readonly CustomRole[] = [];

/**
 * The church's logins and what each can do (ADR 0054; ADR 0036 amendment): roles, built-in or the church's own, and
 * in a multi-campus church the campuses each staff member works with, beside the church-wide switch. users.read sees
 * it (owners and administrators); users.manage changes a person's access; the switch is the owner's (settings.manage).
 */
export function TeamPage() {
  const team = useTeam();
  const custom = useCustomRoles();
  const limit = useCampusLimit();
  const multiCampus = limit.data?.multiCampus === true;
  const campuses = useActiveCampuses(multiCampus);
  const canManage = usePermission("users.manage", { write: true });
  const myRoles = useMyRoles();
  const { userId } = useScope();
  const readerIsOwner = myRoles.data?.keys.includes("tenant_owner") === true;
  const { remember, restore } = useReturnFocus();
  const [search, setSearch] = useState("");
  const [show, setShow] = useState<Show>("team");
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 50 });
  const [open, setOpen] = useState<TeamMember | null>(null);
  const [inviting, setInviting] = useState(false);

  const customRoles = custom.data?.roles ?? NO_ROLES;
  const activeOwners = (team.data ?? []).filter(
    (m) => m.status === "active" && m.roleKeys.includes("tenant_owner"),
  ).length;
  const campusName = useMemo(
    () => new Map((campuses.data ?? []).map((c) => [c.id, c.name])),
    [campuses.data],
  );

  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (team.data ?? []).filter(
      (m) =>
        (show === "everyone" || m.roleKeys.some((k) => !PORTAL_ONLY.has(k))) &&
        (needle.length === 0 ||
          m.displayName.toLowerCase().includes(needle) ||
          (m.email ?? "").toLowerCase().includes(needle)),
    );
  }, [team.data, search, show]);
  const page = rows.slice(
    pagination.pageIndex * pagination.pageSize,
    (pagination.pageIndex + 1) * pagination.pageSize,
  );

  const openMember = useCallback(
    (member: TeamMember) => {
      remember();
      setOpen(member);
    },
    [remember],
  );

  const columns = useMemo<ColumnDef<TeamMember>[]>(
    () => [
      {
        id: "name",
        header: "Name",
        enableSorting: false,
        cell: (ctx) => {
          const m = ctx.row.original;
          const name = memberName(m);
          return (
            <Stack gap={0}>
              {/* A row answers a mouse; the name is the button a keyboard reaches (#261). */}
              <UnstyledButton
                aria-label={`${name}: open access`}
                onClick={(event) => {
                  event.stopPropagation();
                  openMember(m);
                }}
              >
                <Text size="sm" fw={600}>
                  {name}
                </Text>
              </UnstyledButton>
              {m.email !== null && m.email !== name && !isApiKeyLogin(m) ? (
                <Text size="xs" c="dimmed">
                  {m.email}
                </Text>
              ) : null}
            </Stack>
          );
        },
      },
      {
        id: "roles",
        header: "Roles",
        enableSorting: false,
        cell: (ctx) => (
          <Group gap={4} wrap="wrap">
            {ctx.row.original.roleKeys.map((key) => (
              <Badge
                key={key}
                variant="light"
                size="sm"
                color={isPrivilegedRole(key) ? semanticColor("warning") : "gray"}
              >
                {roleName(key, customRoles)}
              </Badge>
            ))}
          </Group>
        ),
      },
      ...(multiCampus
        ? [
            {
              id: "campuses",
              header: "Campuses",
              enableSorting: false,
              cell: (ctx) => {
                const m = ctx.row.original;
                if (m.isPrivileged)
                  return (
                    <Text size="sm" c="dimmed">
                      Every campus (by role)
                    </Text>
                  );
                if (m.campusIds.length === 0)
                  return (
                    <Text size="sm" c="dimmed">
                      Every campus
                    </Text>
                  );
                return (
                  <Text size="sm">
                    {m.campusIds.map((id) => campusName.get(id) ?? "A removed campus").join(", ")}
                  </Text>
                );
              },
            } satisfies ColumnDef<TeamMember>,
          ]
        : []),
      {
        id: "status",
        header: "Status",
        enableSorting: false,
        cell: (ctx) =>
          ctx.row.original.status === "invited" ? (
            <Badge variant="light" color={semanticColor("info")}>
              Invited
            </Badge>
          ) : ctx.row.original.status === "suspended" ? (
            <Badge variant="light" color={semanticColor("warning")}>
              Suspended
            </Badge>
          ) : (
            <Badge variant="light" color={semanticColor("success")}>
              Active
            </Badge>
          ),
      },
    ],
    [customRoles, multiCampus, campusName, openMember],
  );

  return (
    <>
      <PageHeader
        title="Team"
        description="Who can sign in to your church's dashboard, and what each of them can do."
        actions={
          canManage.data === true ? (
            <Button
              onClick={() => {
                remember();
                setInviting(true);
              }}
            >
              Invite someone
            </Button>
          ) : undefined
        }
      />
      <TeamTabs active="people" />
      <Stack gap="lg">
        {limit.isPending ? (
          <LoadingState rows={1} label="Checking campuses" />
        ) : multiCampus && limit.data !== undefined ? (
          <CampusLimitCard on={limit.data.campusLimitsStaff} />
        ) : null}
        <Stack gap="sm">
          <FilterBar
            search={search}
            onSearchChange={(value) => {
              setSearch(value);
              setPagination((p) => ({ ...p, pageIndex: 0 }));
            }}
            searchPlaceholder="Name or email"
            filters={
              <SegmentedControl
                aria-label="Show"
                value={show}
                onChange={(value) => {
                  setShow(value as Show);
                  setPagination((p) => ({ ...p, pageIndex: 0 }));
                }}
                data={[
                  { value: "team", label: "Staff and volunteers" },
                  { value: "everyone", label: "Everyone with a login" },
                ]}
              />
            }
          />
          <DataTable
            label="Team members"
            columns={columns}
            rows={page}
            totalRows={rows.length}
            status={team.isPending ? "loading" : team.isError ? "error" : "ready"}
            pagination={pagination}
            onPaginationChange={setPagination}
            sorting={[]}
            onSortingChange={() => undefined}
            getRowId={(row) => row.membershipId}
            onRowClick={openMember}
            emptyTitle={search.length > 0 ? "Nobody matches" : "Nobody on the team yet"}
            emptyDescription={
              search.length > 0
                ? "Try another name or address."
                : "Choose Invite someone to give a person a login with their roles."
            }
            errorMessage="Could not load your team."
            onRetry={() => {
              void team.refetch();
            }}
          />
        </Stack>
      </Stack>
      <MemberAccessModal
        key={open?.membershipId ?? "closed"}
        member={open}
        customRoles={customRoles}
        campuses={campuses.data ?? []}
        multiCampus={multiCampus}
        readerIsOwner={readerIsOwner}
        canManage={canManage.data === true}
        readerUserId={userId}
        isLastOwner={
          open !== null &&
          open.status === "active" &&
          open.roleKeys.includes("tenant_owner") &&
          activeOwners <= 1
        }
        onClose={() => {
          setOpen(null);
          restore();
        }}
      />
      <InviteMemberModal
        opened={inviting}
        customRoles={customRoles}
        campuses={campuses.data ?? []}
        multiCampus={multiCampus}
        readerIsOwner={readerIsOwner}
        onClose={() => {
          setInviting(false);
          restore();
        }}
      />
    </>
  );
}

/**
 * The church-wide switch: "Limit staff to their campuses". On by default; the owner's to turn off (settings.manage,
 * D5), because it decides what a limited login can reach. Administrators see it and the reason.
 */
function CampusLimitCard({ on }: { readonly on: boolean }) {
  const canChange = usePermission("settings.manage", { write: true });
  const set = useSetCampusLimitsStaff();
  return (
    <Card>
      <Stack gap="xs">
        <Title order={2} size="h4">
          Campuses
        </Title>
        <Switch
          label="Limit staff to their campuses"
          description="A staff member given one or more campuses sees and manages only those campuses' groups, events, check-in and plans, plus anything with no campus. Someone with no campus chosen sees every campus."
          checked={on}
          disabled={canChange.data !== true || set.isPending}
          onChange={(event) => {
            const next = event.currentTarget.checked;
            set.mutate(next, {
              onSuccess: () => {
                notifications.show({
                  color: semanticColor("success"),
                  title: next ? "Campus limits on" : "Campus limits off",
                  message: next
                    ? "Staff with campuses chosen now see only those campuses."
                    : "Every staff member now sees every campus. Their campuses are remembered if you turn this back on.",
                });
              },
              onError: (e) => {
                notifications.show({
                  color: semanticColor("danger"),
                  title: "Not changed",
                  message: describeTeamError(e),
                });
              },
            });
          }}
        />
        <Text size="xs" c="dimmed">
          Owners and administrators always see every campus. The people directory (people,
          households, notes, care and messages) stays church-wide.
          {canChange.data === true ? null : " Only the church's owner can turn this off."}
        </Text>
      </Stack>
    </Card>
  );
}
